import { useState, useCallback, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

interface UseUserFollowResult {
  readonly isFollowing: boolean;
  readonly isLoading: boolean;
  readonly toggleFollow: () => Promise<void>;
}

/**
 * Hook for managing follow/unfollow state for a specific user
 */
export function useUserFollow(targetUserId: string | undefined): UseUserFollowResult {
  const { user: currentUser } = useAuth();
  const [isFollowing, setIsFollowing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const isOwnProfile = currentUser?.id === targetUserId;

  // Check if currently following this user
  useEffect(() => {
    async function checkFollowStatus() {
      if (!currentUser || !targetUserId || isOwnProfile) {
        setIsFollowing(false);
        return;
      }

      const { data } = await supabase
        .from('follows')
        .select('id')
        .eq('follower_id', currentUser.id)
        .eq('following_id', targetUserId)
        .maybeSingle();

      setIsFollowing(!!data);
    }

    checkFollowStatus();
  }, [currentUser, targetUserId, isOwnProfile]);

  const toggleFollow = useCallback(async () => {
    if (!currentUser || !targetUserId || isOwnProfile) return;

    setIsLoading(true);
    try {
      if (isFollowing) {
        // Unfollow
        await supabase
          .from('follows')
          .delete()
          .eq('follower_id', currentUser.id)
          .eq('following_id', targetUserId);
        setIsFollowing(false);
      } else {
        // Follow
        await supabase
          .from('follows')
          .insert({
            follower_id: currentUser.id,
            following_id: targetUserId,
          });
        setIsFollowing(true);
      }
    } catch (error) {
      console.error('Error toggling follow:', error);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, targetUserId, isFollowing, isOwnProfile]);

  return {
    isFollowing,
    isLoading,
    toggleFollow,
  };
}
